import { Box, Tooltip, Typography, LinearProgress } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Link as RouterLink } from 'react-router-dom'
import LazyImage from '../lazy-image'
import { showProgressIndicators } from '../../utils/progress-visibility'
import { useAuth } from '../../context/AuthContext'

interface SeasonCardProps {
  id: number
  name: string
  posterUrl?: string
  seasonNumber: number
  episodeCount: number
  watchedCount?: number
  to: string
}

// A real anchor so right-click / middle-click / cmd+click open in a new tab.
const Wrapper = styled(RouterLink)(({ theme }) => ({
  display: 'block',
  textDecoration: 'none',
  color: 'inherit',
  cursor: 'pointer',
  width: 180,
  flexShrink: 0,
  borderRadius: Number(theme.shape.borderRadius) * 2,
  overflow: 'hidden',
  border: `2px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  transition: 'transform 0.25s ease, border-color 0.25s ease',
  '@media (hover: hover) and (pointer: fine)': {
    '&:hover': {
      transform: 'scale(1.03)',
      borderColor: theme.palette.brand.main,
    },
  },
}))

const Poster = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 260,
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
}))

const PosterFallback = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 48,
  backgroundColor: theme.palette.background.default,
}))

const Info = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
}))

const Title = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 600,
  color: theme.palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}))

const Meta = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.text.secondary,
}))

const ProgressWrapper = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
}))

const TitleRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}))

const ProgressPercent = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.text.secondary,
}))

const SeasonProgressBar = styled(LinearProgress)({
  height: 8,
  borderRadius: 4,
})

// Keeps card heights aligned when a card has no progress bar to show
const ProgressPlaceholder = styled(Box)({
  height: 8,
})

export default function SeasonCard({
  id,
  name,
  posterUrl,
  seasonNumber,
  episodeCount,
  watchedCount = 0,
  to,
}: SeasonCardProps) {
  const progress = episodeCount > 0 ? Math.min(100, (watchedCount / episodeCount) * 100) : 0
  const showProgress = showProgressIndicators() && seasonNumber > 0
  const { isAuthenticated } = useAuth()

  return (
    <Wrapper to={to}>
      <Poster>
        <LazyImage
          src={posterUrl}
          alt={name}
          fallback={
            <PosterFallback>
              {name.slice(0, 1).toUpperCase() || '?'}
            </PosterFallback>
          }
        />
      </Poster>

      <Info>
        <TitleRow>
          <Title>{name}</Title>
          {showProgress && (
            <Tooltip title={`${watchedCount}/${episodeCount} episodes watched`}>
              <ProgressPercent>{Math.floor(progress)}%</ProgressPercent>
            </Tooltip>
          )}
        </TitleRow>
        <Meta>
          Season {seasonNumber} • {episodeCount} eps
        </Meta>
        {isAuthenticated && (
          <ProgressWrapper>
            {showProgress ? (
              <SeasonProgressBar variant="determinate" value={progress} />
            ) : (
              <ProgressPlaceholder />
            )}
          </ProgressWrapper>
        )}
      </Info>
    </Wrapper>
  )
}
